/**
 * Gifts page: the custom elements behind the Gifts template sections. Loaded only on collection
 * templates with the "gifts" suffix (snippets/gifts-head.liquid), after pets-core.js and gifts-core.js.
 *
 * <gifts-finder>  the three-step gift quiz and its answer state (sections/gifts-finder.liquid)
 * <gifts-picks>   picks and close matches cloned from the loaded grid
 *
 * Storage: "lumi:gift" holds the answers {for, vibe, budget}. Answers in the URL (?for=&vibe=&budget=) win
 * over the saved ones.
 * Events: emits "lumi:gifts:answers" with { for, vibe, maxCents } whenever the answers are complete (on
 * finishing, on load with saved or linked answers, and after a pill changes one), and "lumi:gifts:reset"
 * when Start over clears them. The load-time answers go out while <gifts-finder> connects, before
 * <gifts-picks> is defined, so the picks read them from the finder (currentAnswers) when they connect.
 */
(function () {
  const Lumi = (window.Lumi = window.Lumi || {});
  const core = Lumi.giftsCore;
  if (!core) return;

  const STORAGE_KEY = 'lumi:gift';
  const STEPS = ['for', 'vibe', 'budget'];
  const ANY = 'any';

  const readSaved = () => {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return null;
    }
  };

  const clearSaved = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* storage unavailable: nothing was saved */
    }
  };

  // The loaded grid cards with their gift data as core items. Read on demand, since the grid changes after
  // sorting, filtering and Load more; cards a swap is fading out (data-leaving) no longer count.
  const gridCards = () => {
    const split = (value) => (value || '').split(/\s+/).filter(Boolean);
    const cards = [];
    document.querySelectorAll('[data-grid] > :not([data-leaving]) .product-card').forEach((card) => {
      const priceCents = Number(card.dataset.giftPrice);
      if (!Number.isFinite(priceCents)) return;
      cards.push({
        card,
        item: {
          id: card.dataset.productId,
          for: split(card.dataset.giftFor),
          vibes: split(card.dataset.giftVibe),
          priceCents,
          available: !card.hasAttribute('data-sold-out'),
        },
      });
    });
    return cards;
  };

  // A locale template with [name] slots filled in. Empty slots leave no stray spaces before punctuation.
  const fill = (template, values) =>
    (template || '')
      .replace(/\[(\w+)\]/g, (slot, name) => (Object.prototype.hasOwnProperty.call(values, name) ? values[name] : slot))
      .replace(/\s+/g, ' ')
      .replace(/ ([.,;:!?])/g, (match, mark) => mark)
      .trim();

  // A budget in the shop's money format, without the cents when it is a round amount ("$30", not "$30.00").
  const money = (cents) => {
    const format = Lumi.moneyFormat || '${{amount}}';
    if (cents % 100 !== 0) return Lumi.formatMoney(cents, format);
    const whole = format.replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, (match, comma) => (comma ? '{{amount_no_decimals_with_comma_separator}}' : '{{amount_no_decimals}}'));
    return Lumi.formatMoney(cents, whole);
  };

  /* ---------- Finder: who it's for, what they're like, the budget ---------- */
  class GiftsFinder extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-gifts-config]');
      let config = null;
      try {
        config = configEl ? JSON.parse(configEl.textContent) : null;
      } catch (e) {
        config = null;
      }
      if (!config) return;

      this.config = config;
      // The locale filter HTML-escapes its text (it's becomes it&#39;s); the script sets text, so decode it.
      const decoder = document.createElement('textarea');
      this.strings = {};
      Object.keys(config.strings || {}).forEach((key) => {
        decoder.innerHTML = config.strings[key];
        this.strings[key] = decoder.value;
      });
      this.options = {
        recipients: config.recipients.map((r) => r.key),
        vibes: config.vibes.map((v) => v.key),
        budgets: config.budgets.map((b) => b.key),
      };
      this.card = this.querySelector('[data-gifts-card]');
      this.intro = this.querySelector('[data-gifts-intro]');
      this.done = this.querySelector('[data-gifts-done]');
      this.pillList = this.querySelector('[data-gifts-pills]');
      this.backButton = this.querySelector('[data-gifts-back]');
      this.panels = {};
      STEPS.forEach((step) => {
        this.panels[step] = this.querySelector(`[data-gifts-step="${step}"]`);
      });
      this.pills = {};
      STEPS.forEach((step) => {
        const pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'chip-pill';
        pill.dataset.giftsPill = step;
        this.pills[step] = pill;
      });

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);

      // Answers in the link win over the saved ones; a missing step starts the quiz there.
      const saved = core.parseState(readSaved(), this.options);
      const linked = core.fromParams(window.location.search, this.options);
      this.answers = Object.assign({ for: null, vibe: null, budget: null }, saved, linked);
      this.show(this.nextStep());
      if (!this.nextStep()) this.emitAnswers();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    /* ----- Data ----- */

    items() {
      return gridCards().map((entry) => entry.item);
    }

    // The answers as the picks match them, or null while a step is still unanswered.
    currentAnswers() {
      if (!this.config || this.nextStep()) return null;
      return { for: this.answers.for, vibe: this.answers.vibe, maxCents: this.maxCents() };
    }

    maxCents() {
      const budget = this.config.budgets.find((b) => b.key === this.answers.budget);
      return budget && Number.isFinite(budget.maxCents) ? budget.maxCents : null;
    }

    labelFor(step, key) {
      const list = { for: this.config.recipients, vibe: this.config.vibes, budget: this.config.budgets }[step];
      const entry = list.find((e) => e.key === key);
      if (entry && entry.label) return entry.label;
      return this.strings[`any_${step}`] || key;
    }

    // The first step still without an answer, or null when all three are answered.
    nextStep() {
      return STEPS.find((step) => this.answers[step] === null) || null;
    }

    /* ----- Events ----- */

    onClick(e) {
      const tileEl = e.target.closest('.gifts-tile');
      if (tileEl && this.card.contains(tileEl)) {
        const step = tileEl.closest('[data-gifts-step]').dataset.giftsStep;
        this.answer(step, tileEl.dataset.key);
        return;
      }
      if (e.target.closest('[data-gifts-skip]')) {
        this.answer(this.current, ANY);
        return;
      }
      if (e.target.closest('[data-gifts-back]')) {
        this.show(STEPS[STEPS.indexOf(this.current) - 1], true);
        return;
      }
      const pill = e.target.closest('[data-gifts-pill]');
      if (pill) {
        this.show(pill.dataset.giftsPill, true);
        return;
      }
      if (e.target.closest('[data-gifts-reset]')) this.reset();
    }

    answer(step, key) {
      this.answers[step] = key;
      if (step === 'for' && this.answers.vibe !== null && this.answers.vibe !== ANY) {
        // A new recipient can make the earlier vibe a stranger: ask again rather than keep it.
        if (this.vibeKeys().indexOf(this.answers.vibe) === -1) this.answers.vibe = null;
      }
      const next = this.nextStep();
      if (next) {
        this.show(next, true);
        return;
      }
      Lumi.storage.set(STORAGE_KEY, { for: this.answers.for, vibe: this.answers.vibe, budget: this.answers.budget });
      this.show(null, true);
      this.emitAnswers();
    }

    reset() {
      this.answers = { for: null, vibe: null, budget: null };
      clearSaved();
      this.show('for', true);
      Lumi.emit('lumi:gifts:reset');
    }

    emitAnswers() {
      Lumi.emit('lumi:gifts:answers', this.currentAnswers());
    }

    /* ----- Views ----- */

    vibeKeys() {
      return core.vibesFor(this.answers.for === null ? ANY : this.answers.for, this.config.vibes, this.items());
    }

    // Shows one step, or the answer state when step is null. The tiles are updated in place, never rebuilt.
    show(step, moveFocus) {
      this.current = step;
      const done = step === null;
      this.card.hidden = done;
      this.intro.hidden = done;
      this.backButton.hidden = done || step === 'for';

      STEPS.forEach((name, index) => {
        const panel = this.panels[name];
        panel.hidden = name !== step;
        panel.querySelectorAll('.gifts-tile').forEach((tileEl) => {
          tileEl.setAttribute('aria-pressed', String(tileEl.dataset.key === this.answers[name]));
        });
        const dot = this.querySelector(`[data-gifts-dot="${name}"]`);
        if (dot) {
          dot.classList.toggle('is-on', name === step);
          dot.classList.toggle('is-done', !done && STEPS.indexOf(step) > index);
        }
      });

      if (step === 'vibe') {
        const offered = this.vibeKeys();
        this.panels.vibe.querySelectorAll('.gifts-tile').forEach((tileEl) => {
          tileEl.hidden = offered.indexOf(tileEl.dataset.key) === -1;
        });
      }

      if (done) {
        // Unhide first, then write the headline: a live region announces text added to a region that is already shown.
        this.done.hidden = false;
        this.renderAnswers();
      } else {
        this.done.hidden = true;
      }

      if (moveFocus) {
        const target = done ? this.pills.for : this.panels[step].querySelector('h2');
        if (target) target.focus();
      }
    }

    // The pills. The headline above them is written by <gifts-picks>, which knows how many gifts fit.
    renderAnswers() {
      STEPS.forEach((step) => {
        const pill = this.pills[step];
        const label = this.labelFor(step, this.answers[step]);
        pill.textContent = label;
        pill.setAttribute('aria-label', (this.strings.change || '').replace('[step]', this.strings[`step_${step}`] || '').replace('[answer]', label));
        if (pill.parentNode !== this.pillList) this.pillList.appendChild(pill);
      });
    }
  }

  /* ---------- Picks: the gifts that fit the answers, then close matches (sections/gifts-picks.liquid) ---------- */
  class GiftsPicks extends HTMLElement {
    connectedCallback() {
      this.picksList = this.querySelector('[data-gifts-picks]');
      this.closeWrap = this.querySelector('[data-gifts-close-wrap]');
      this.closeList = this.querySelector('[data-gifts-close]');
      if (!this.picksList || !this.closeList) return;

      this.onAnswers = (e) => {
        this.answers = e.detail || null;
        this.render();
      };
      this.onReset = () => {
        this.answers = null;
        this.render();
      };
      document.addEventListener('lumi:gifts:answers', this.onAnswers);
      document.addEventListener('lumi:gifts:reset', this.onReset);

      // facets.js reuses, adds or drops grid items after sorting, filtering and Load more: recompute after each swap.
      const grid = document.querySelector('[data-grid]');
      if (grid) {
        this.observer = new MutationObserver(() => {
          if (this.answers) this.render();
        });
        this.observer.observe(grid, { childList: true });
      }

      // The finder sends saved or linked answers while it connects, before this element exists: ask it.
      const finder = this.finder();
      this.answers = finder && finder.currentAnswers ? finder.currentAnswers() : null;
      if (this.answers) this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('lumi:gifts:answers', this.onAnswers);
      document.removeEventListener('lumi:gifts:reset', this.onReset);
      if (this.observer) this.observer.disconnect();
    }

    finder() {
      return document.querySelector('gifts-finder');
    }

    // A recipient's or vibe's label as the finder shows it, lowercased for the middle of a sentence.
    label(step, key) {
      const finder = this.finder();
      const config = finder && finder.config;
      const list = config ? (step === 'for' ? config.recipients : config.vibes) : [];
      const entry = list.find((e) => e.key === key);
      return ((entry && entry.label) || key.replace(/-/g, ' ')).toLocaleLowerCase();
    }

    render() {
      const answers = this.answers;
      if (!answers) {
        this.picksList.replaceChildren();
        this.closeList.replaceChildren();
        this.closeWrap.hidden = true;
        this.hidden = true;
        return;
      }

      const cards = gridCards();
      const result = core.match(cards.map((entry) => entry.item), answers);
      const cardOf = new Map(cards.map((entry) => [entry.item, entry.card]));
      // Each product once, even if the grid ever holds two cards for it.
      const shown = new Set();
      const clones = (items) =>
        items
          .filter((item) => {
            if (shown.has(item.id)) return false;
            shown.add(item.id);
            return true;
          })
          .map((item) => this.cloneCard(cardOf.get(item), item, answers));
      this.picksList.replaceChildren(...clones(result.picks));
      this.closeList.replaceChildren(...clones(result.close));

      const picks = this.picksList.children.length;
      const close = this.closeList.children.length;
      this.closeWrap.hidden = close === 0;
      this.hidden = picks === 0 && close === 0;
      this.writeHeadline(answers, picks, close);
    }

    // A copy of the grid item around a card, with the reason it was chosen. Ids stay unique to the grid,
    // and inline styles from a running grid animation are not copied.
    cloneCard(card, item, answers) {
      const source = card.closest('[data-grid] > *') || card;
      const clone = source.cloneNode(true);
      clone.removeAttribute('style');
      clone.removeAttribute('data-flip-id');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      const text = this.reasonText(item, answers);
      if (text) {
        const why = document.createElement('span');
        why.className = 'gifts-why';
        why.textContent = text;
        const cardCopy = clone.matches('.product-card') ? clone : clone.querySelector('.product-card');
        (cardCopy.querySelector('.product-card__info') || cardCopy).appendChild(why);
      }
      return clone;
    }

    // "cozy · under $30" for a pick, "cozy · a bit over" past the budget, the card's own vibe ("sentimental")
    // for a close match with another vibe, and the budget alone when the shopper had no vibe in mind.
    reasonText(item, answers) {
      const why = core.reason(item, answers);
      const words = this.dataset;
      const vibe = why.vibe ? this.label('vibe', why.vibe) : '';
      const price = answers.maxCents === null ? '' : money(answers.maxCents);
      // core.reason gives no vibe when the shopper had none in mind (any): the reason is then the budget alone.
      let template = '';
      if (vibe && why.budget === 'over') template = words.reasonOver;
      else if (vibe && why.budget === 'under' && why.vibe === answers.vibe) template = words.reasonUnder;
      else if (vibe) template = words.reasonVibe;
      else if (why.budget === 'under') template = words.reasonPrice;
      else if (why.budget === 'over') template = words.reasonBitOver;
      return fill(template, { vibe, price });
    }

    // "2 cozy picks for a pet lover under $30", or, with no picks, "No ... yet" with what shows instead.
    writeHeadline(answers, picks, close) {
      const headline = document.querySelector('[data-gifts-headline]');
      if (!headline) return;
      const words = this.dataset;
      const values = {
        count: String(picks),
        vibe: answers.vibe === ANY ? '' : this.label('vibe', answers.vibe),
        recipient: answers.for === ANY ? '' : fill(words.headlineFor, { name: this.label('for', answers.for) }),
        budget: answers.maxCents === null ? '' : fill(words.headlineBudget, { price: money(answers.maxCents) }),
      };
      let template = words.headlineEmpty;
      if (picks === 1) template = words.headlineOne;
      else if (picks > 1) template = words.headline;
      else if (close > 0) template = words.headlineNone;
      const text = fill(template, values);
      // Rewriting the same words would make screen readers announce them again after every grid swap.
      if (headline.textContent !== text) headline.textContent = text;
    }
  }

  if (!customElements.get('gifts-finder')) customElements.define('gifts-finder', GiftsFinder);
  if (!customElements.get('gifts-picks')) customElements.define('gifts-picks', GiftsPicks);
})();
